import { getStringFromItem, Shell } from '@components/AutoComplete';
import { notrans } from '@utils/localize';
import { useCallback } from 'react';
import { useIndexedDBStore } from '../useIndexedDB';
import { RECENT_SELECTIONS_IDB_STORE } from '../useIndexedDB/config';

/**
 * Hook to manage recent selections in AutoComplete components.
 * Uses IndexedDB to persist selections across sessions.
 *
 * @param key - Unique identifier for this autocomplete instance
 * @param maxItems - Maximum number of recent items to keep (default: 10)
 */
export function useRecentSelections<ItemType>(
  key: string,
  maxItems: number = 10,
): {
  loadRecentSelections: () => Promise<
    Array<Shell<ItemType> & { timestamp: number }>
  >;
  saveRecentSelection: (item: Shell<ItemType>) => Promise<void>;
  clearRecentSelections: () => Promise<void>;
} {
  const recentSelectionsDB = useIndexedDBStore(RECENT_SELECTIONS_IDB_STORE);

  const loadRecentSelections = useCallback(async (): Promise<
    Array<Shell<ItemType> & { timestamp: number }>
  > => {
    try {
      const stored = await recentSelectionsDB.getByID(key);
      if (!stored?.selections?.length) {
        return [];
      }

      // Convert stored selections back to Shell items with timestamps
      return stored.selections.map((selection) => ({
        id: selection.id,
        label: selection.label,
        value: selection.value as ItemType | undefined,
        timestamp: selection.timestamp,
      }));
    } catch (error) {
      // Graceful degradation if IndexedDB fails
      // eslint-disable-next-line no-console
      console.warn(notrans('Failed to load recent selections:'), error);
      return [];
    }
  }, [key, recentSelectionsDB]);

  const saveRecentSelection = useCallback(
    async (item: Shell<ItemType>): Promise<void> => {
      try {
        // Get current selections
        const stored = await recentSelectionsDB.getByID(key);
        const currentSelections = stored?.selections || [];

        // Get plain text label
        const label = getStringFromItem(item);
        const itemId = item.id ?? label;

        // Remove this item if it already exists (we'll add it to the front)
        const filteredSelections = currentSelections.filter(
          (s) => s.id !== itemId,
        );

        // Add new selection to the front
        const newSelection = {
          id: itemId,
          label,
          value: item.value,
          timestamp: Date.now(),
        };

        const updatedSelections = [newSelection, ...filteredSelections].slice(
          0,
          maxItems,
        );

        // Save to IndexedDB
        await recentSelectionsDB.update({
          key,
          selections: updatedSelections,
          maxItems,
          lastUpdated: Date.now(),
        });
      } catch (error) {
        // Graceful degradation if IndexedDB fails
        // eslint-disable-next-line no-console
        console.warn(notrans('Failed to save recent selection:'), error);
      }
    },
    [key, maxItems, recentSelectionsDB],
  );

  const clearRecentSelections = useCallback(async (): Promise<void> => {
    try {
      await recentSelectionsDB.deleteByID(key);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(notrans('Failed to clear recent selections:'), error);
    }
  }, [key, recentSelectionsDB]);

  return {
    loadRecentSelections,
    saveRecentSelection,
    clearRecentSelections,
  };
}
